// components/AdminDashboardHome.js - Panneau Admin avec design HomePage pour tous les modules admin/manager
import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useTranslation } from '../i18n/LanguageContext';
import { useAuth } from '../App';
import { API_BASE } from '../config';
import {
  Shield, Users, BarChart3, TrendingUp, Wallet, Calendar, 
  QrCode, Camera, CreditCard, Sparkles, MapPin,
  DollarSign, UserPlus, CheckCircle, XCircle,
  Home, LogOut, PanelRightOpen, X
} from 'lucide-react';

// Fallback: jab API se features na aayein to Starter plan modules dikhao
const DEFAULT_PLAN_FEATURES = ['pointage', 'historique', 'communiques', 'dashboard', 'employees', 'reports', 'documents', 'profil', 'audit_photos', 'ideas', 'performance'];

const hasFeature = (enabledFeatures, feature, billingLoaded = true) => {
  if (!billingLoaded) return false;
  const list = (enabledFeatures && enabledFeatures.length > 0) ? enabledFeatures : DEFAULT_PLAN_FEATURES;
  return list.includes(feature);
};

const AdminDashboardHome = ({ user, onNavigate, onLogout }) => {
  const { t, language } = useTranslation();
  const dateLocale = language === 'fr' ? 'fr-FR' : 'en-GB';
  const { refreshUser, getAuthHeaders, showSuccess } = useAuth();
  const { enabledFeatures = [], billingLoaded = false, onRefetchBilling } = useOutletContext() || {};
  const [currentTime, setCurrentTime] = useState(new Date());
  const [mounted, setMounted] = useState(false);

  // Company + subscription: refetch billing on mount for subscription-based modules
  useEffect(() => {
    if (refreshUser && user) refreshUser();
    if (onRefetchBilling) onRefetchBilling();
  }, [onRefetchBilling]);
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.innerWidth <= 768;
    }
    return false;
  });
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [stats, setStats] = useState({
    pendingLeaves: 0,
    pendingExpenses: 0,
    pendingRegistrations: 0,
    totalEmployees: 0
  });
  const [logoUploading, setLogoUploading] = useState(false);
  const [logoError, setLogoError] = useState('');

  useEffect(() => {
    setMounted(true);
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);
    loadAdminStats();
    
    const handleResize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    
    return () => {
      clearInterval(timer);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    if (!isMobile && mobileDrawerOpen) setMobileDrawerOpen(false);
  }, [isMobile, mobileDrawerOpen]);

  const loadAdminStats = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Charger les stats en parallèle - assigned-to-me = leave requests needing MY approval (real-time)
      const [leavesRes, expensesRes, registrationsRes, employeesRes] = await Promise.all([
        fetch(`${API_BASE}/api/leaves/assigned-to-me`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }).catch(() => null),
        fetch(`${API_BASE}/api/notes-frais/pending-validation`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }).catch(() => null),
        fetch(`${API_BASE}/api/admin/pending-requests`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }).catch(() => null),
        fetch(`${API_BASE}/api/employees?all=true&active=all`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }).catch(() => null)
      ]);

      if (leavesRes?.ok) {
        const data = await leavesRes.json();
        const count = (data.success && Array.isArray(data.requests)) ? data.requests.length : 0;
        setStats(prev => ({ ...prev, pendingLeaves: count }));
      }
      if (expensesRes?.ok) {
        const data = await expensesRes.json();
        setStats(prev => ({ ...prev, pendingExpenses: data.expenses?.length || data.length || 0 }));
      }
      if (registrationsRes?.ok) {
        const data = await registrationsRes.json();
        setStats(prev => ({ ...prev, pendingRegistrations: data.requests?.length || 0 }));
      }
      if (employeesRes?.ok) {
        const data = await employeesRes.json();
        // API returns { data: [...], pagination: { total } } - use total for company employee count
        const total = data.pagination?.total ?? data.employees?.length ?? data.data?.length ?? 0;
        setStats(prev => ({ ...prev, totalEmployees: total }));
      }
    } catch (error) {
      console.error('Erreur chargement stats admin:', error);
    }
  };

  const formatDate = (date) => {
    return date.toLocaleDateString(dateLocale, { 
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getGreeting = () => {
    const hour = currentTime.getHours();
    if (hour < 12) return t('home.good_morning') || 'Bonjour';
    if (hour < 18) return t('home.good_afternoon') || 'Bon après-midi';
    return t('home.good_evening') || 'Bonsoir';
  };


  const handleCompanyLogoChange = async (event) => {
    const file = event.target.files && event.target.files[0];
    if (!file) return;
    setLogoError('');
    setLogoUploading(true);
    try {
      const tokenHeaders = getAuthHeaders();
      const formData = new FormData();
      formData.append('logo', file);
      const response = await fetch(`${API_BASE}/api/company/logo`, {
        method: 'POST',
        headers: { Authorization: tokenHeaders.Authorization },
        body: formData
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.success) {
        setLogoError(data.error || 'Erreur lors du téléversement du logo');
      } else {
        showSuccess && showSuccess(t('common.saved_successfully') || 'Logo mis à jour');
        await refreshUser?.();
      }
    } catch (e) {
      console.error('❌ Erreur upload logo:', e);
      setLogoError('Erreur lors du téléversement du logo');
    } finally {
      setLogoUploading(false);
      event.target.value = '';
    }
  };

  const handleAppClick = (appId) => {
    if (onNavigate) {
      onNavigate(appId);
    }
  };

  // Palette de couleurs pour les modules admin
  const colorPalette = {
    dashboard: { gradient: 'linear-gradient(135deg, #3f51b5, #7986cb)', shadow: 'rgba(63, 81, 181, 0.3)' },
    reports: { gradient: 'linear-gradient(135deg, #8b5cf6, #a78bfa)', shadow: 'rgba(139, 92, 246, 0.3)' },
    soldes: { gradient: 'linear-gradient(135deg, #06b6d4, #22d3ee)', shadow: 'rgba(6, 182, 212, 0.3)' },
    planning: { gradient: 'linear-gradient(135deg, #6366f1, #8b5cf6)', shadow: 'rgba(99, 102, 241, 0.3)' },
    employees: { gradient: 'linear-gradient(135deg, #2196f3, #64b5f6)', shadow: 'rgba(33, 150, 243, 0.3)' },
    badges: { gradient: 'linear-gradient(135deg, #10b981, #34d399)', shadow: 'rgba(16, 185, 129, 0.3)' },
    'audit-photos': { gradient: 'linear-gradient(135deg, #8b5cf6, #a78bfa)', shadow: 'rgba(139, 92, 246, 0.3)' },
    facturation: { gradient: 'linear-gradient(135deg, #ec4899, #f472b6)', shadow: 'rgba(236, 72, 153, 0.3)' },
    abonnements: { gradient: 'linear-gradient(135deg, #f59e0b, #fbbf24)', shadow: 'rgba(245, 158, 11, 0.3)' },
    gps: { gradient: 'linear-gradient(135deg, #06b6d4, #0891b2)', shadow: 'rgba(6, 182, 212, 0.3)' },
    'admin-panel': { gradient: 'linear-gradient(135deg, #6366f1, #8b5cf6)', shadow: 'rgba(99, 102, 241, 0.3)' },
    'leaves-admin': { gradient: 'linear-gradient(135deg, #00e676, #00b85c)', shadow: 'rgba(0, 230, 118, 0.3)' },
    'expenses-admin': { gradient: 'linear-gradient(135deg, #ffc107, #ffd54f)', shadow: 'rgba(255, 193, 7, 0.3)' },
    'registrations': { gradient: 'linear-gradient(135deg, #667eea, #764ba2)', shadow: 'rgba(102, 126, 234, 0.3)' },
    'accounts': { gradient: 'linear-gradient(135deg, #14b8a6, #06b6d4)', shadow: 'rgba(20, 184, 166, 0.3)' },
    payroll: { gradient: 'linear-gradient(135deg, #f97316, #fb923c)', shadow: 'rgba(249, 115, 22, 0.3)' }
  };

  return (
    <div style={{
      minHeight: '100vh',
      width: '100%',
      background: 'linear-gradient(180deg, #0a0e27 0%, #1a1f3a 100%)',
      color: '#ffffff',
      fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", sans-serif',
      overflowX: 'hidden',
      paddingBottom: 'env(safe-area-inset-bottom, 0)',
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      overflowY: 'auto'
    }}>
      <style>{`
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .admin-app-icon {
          animation: slideUp 0.4s ease-out backwards;
        }
        .admin-app-icon:nth-child(1) { animation-delay: 0.05s; }
        .admin-app-icon:nth-child(2) { animation-delay: 0.1s; }
        .admin-app-icon:nth-child(3) { animation-delay: 0.15s; }
        .admin-app-icon:nth-child(4) { animation-delay: 0.2s; }
        .admin-app-icon:nth-child(5) { animation-delay: 0.25s; }
        .admin-app-icon:nth-child(6) { animation-delay: 0.3s; }
        .admin-app-icon:nth-child(7) { animation-delay: 0.35s; }
        .admin-app-icon:nth-child(8) { animation-delay: 0.4s; }
        .admin-app-icon:nth-child(9) { animation-delay: 0.45s; }
        .admin-app-icon:nth-child(10) { animation-delay: 0.5s; }
        .admin-app-icon:nth-child(11) { animation-delay: 0.55s; }
        .admin-app-icon:nth-child(12) { animation-delay: 0.6s; }
        .admin-app-icon:nth-child(13) { animation-delay: 0.65s; }
        .admin-app-icon:nth-child(14) { animation-delay: 0.7s; }
        .admin-app-icon:nth-child(15) { animation-delay: 0.75s; }
        .admin-app-icon:nth-child(16) { animation-delay: 0.8s; }
        
        @media (max-width: 767px) {
          .admin-grid {
            grid-template-columns: repeat(2, 1fr) !important;
            padding: 180px 16px 100px !important;
            gap: 16px !important;
          }
          .admin-app-icon {
            min-height: 120px !important;
            padding: 20px 12px !important;
          }
          .admin-app-icon > div:first-of-type {
            font-size: 48px !important;
          }
          .admin-app-icon > div:last-of-type {
            font-size: 16px !important;
            font-weight: 600 !important;
            padding: 0 12px !important;
            line-height: 1.3 !important;
          }
          .admin-large-widget {
            padding: 28px !important;
            min-height: 140px !important;
          }
        }
        @media (min-width: 768px) {
          .admin-grid {
            grid-template-columns: repeat(4, 1fr) !important;
            padding: 200px 40px 120px !important;
            gap: 24px !important;
          }
          .admin-app-icon {
            min-height: 120px !important;
          }
          .admin-app-icon:hover {
            transform: translateY(-6px) scale(1.02) !important;
            box-shadow: 0 12px 32px rgba(0,0,0,0.4), 0 4px 16px rgba(0,0,0,0.2) !important;
          }
        }
        @media (min-width: 1024px) {
          .admin-grid {
            grid-template-columns: repeat(5, 1fr) !important;
            padding: 200px 60px 120px !important;
            gap: 28px !important;
          }
          .admin-app-icon {
            min-height: 140px !important;
          }
        }
        @media (min-width: 1440px) {
          .admin-grid {
            grid-template-columns: repeat(6, 1fr) !important;
            padding: 220px 100px 140px !important;
            gap: 32px !important;
          }
          .admin-app-icon {
            min-height: 160px !important;
          }
        }
      `}</style>

      {/* Header: on mobile minimal bar + drawer; on desktop full header */}
      {isMobile ? (
        <>
          <div className="admin-header" style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            padding: '12px 16px',
            background: 'transparent',
            backdropFilter: 'none',
            borderBottom: 'none',
            zIndex: 999,
            maxWidth: '100%'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0 }}>
                {(user?.company_logo_url || user?.companyLogoUrl) && (
                  <div
                    title={t('common.change_company_logo') || 'Change company logo'}
                    style={{
                      width: 32,
                      height: 32,
                      borderRadius: '50%',
                      overflow: 'hidden',
                      border: '1px solid rgba(255,255,255,0.3)',
                      flexShrink: 0,
                      background: 'rgba(15,23,42,0.8)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <img
                      src={(() => {
                        const logo = user.company_logo_url || user.companyLogoUrl;
                        return /^https?:\/\//i.test(String(logo || '')) ? logo : `${API_BASE.replace(/\/$/, '')}/${logo}`;
                      })()}
                      alt={user?.company || user?.companyName || 'Logo'}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  </div>
                )}
                <div style={{ fontSize: '18px', fontWeight: 700, letterSpacing: '-0.5px', color: '#ffffff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {user?.company || user?.companyName || 'Admin'}
                </div>
              </div>
              <button
                type="button"
                onClick={() => setMobileDrawerOpen(true)}
                aria-label={t('home.open_menu') || t('common.menu') || 'Open menu'}
                title={t('home.open_menu') || t('common.menu') || 'Open menu'}
                style={{
                  background: 'rgba(255, 255, 255, 0.1)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  color: '#ffffff',
                  padding: '10px',
                  borderRadius: '12px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backdropFilter: 'blur(10px)',
                  flexShrink: 0,
                  width: '44px',
                  height: '44px'
                }}
              >
                <PanelRightOpen size={22} aria-hidden />
              </button>
            </div>
          </div>

          {/* Mobile drawer: top bar data */}
          {mobileDrawerOpen && (
            <div
              role="presentation"
              onClick={() => setMobileDrawerOpen(false)}
              style={{
                position: 'fixed',
                inset: 0,
                background: 'rgba(0,0,0,0.5)',
                zIndex: 2000,
                display: 'flex',
                justifyContent: 'flex-end'
              }}
            >
              <div
                role="dialog"
                aria-modal="true"
                aria-label={t('common.menu') || 'Menu'}
                onClick={(e) => e.stopPropagation()}
                style={{
                  width: 'min(320px, 88vw)',
                  height: '100%',
                  background: 'linear-gradient(180deg, #0a0e27 0%, #1a1f3a 100%)',
                  backdropFilter: 'blur(24px)',
                  borderLeft: '1px solid rgba(255,255,255,0.12)',
                  padding: '20px 16px',
                  boxSizing: 'border-box',
                  overflowY: 'auto'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
                  <span style={{ fontWeight: 700, fontSize: '16px', color: '#fff' }}>{t('common.menu') || 'Menu'}</span>
                  <button
                    type="button"
                    onClick={() => setMobileDrawerOpen(false)}
                    aria-label={t('common.close') || 'Close'}
                    style={{
                      background: 'rgba(255, 255, 255, 0.1)',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      color: '#fff',
                      width: '40px',
                      height: '40px',
                      borderRadius: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer'
                    }}
                  >
                    <X size={20} />
                  </button>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px' }}>
                  {(user?.company_logo_url || user?.companyLogoUrl) && (
                    <div
                      title={t('common.change_company_logo') || 'Change company logo'}
                      style={{
                        width: 32,
                        height: 32,
                        borderRadius: '50%',
                        overflow: 'hidden',
                        border: '1px solid rgba(255,255,255,0.3)',
                        flexShrink: 0,
                        background: 'rgba(15,23,42,0.8)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <img
                        src={(() => {
                          const logo = user.company_logo_url || user.companyLogoUrl;
                          return /^https?:\/\//i.test(String(logo || '')) ? logo : `${API_BASE.replace(/\/$/, '')}/${logo}`;
                        })()}
                        alt={user?.company || user?.companyName || 'Logo'}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    </div>
                  )}
                  <div style={{ fontSize: '20px', fontWeight: 700, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {getGreeting()} {user?.first_name || 'Admin'}{(user?.company || user?.companyName) ? ` - ${user?.company || user?.companyName}` : ''}
                  </div>
                </div>
                <div style={{ fontSize: '14px', color: 'rgba(255,255,255,0.7)', marginBottom: '16px' }}>
                  {formatDate(currentTime)}
                </div>
                <div style={{ display: 'flex', gap: '10px', marginBottom: '16px', flexWrap: 'wrap' }}>
                  <button
                    onClick={() => { setMobileDrawerOpen(false); onNavigate && onNavigate('home'); }}
                    style={{
                      background: 'rgba(255, 255, 255, 0.1)',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      color: '#ffffff',
                      padding: '8px 14px',
                      borderRadius: '10px',
                      fontSize: '13px',
                      fontWeight: 600,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      backdropFilter: 'blur(10px)'
                    }}
                  >
                    <Home size={16} />
                    <span>{t('navigation.home')}</span>
                  </button>
                  {onLogout && (
                    <button
                      onClick={() => { setMobileDrawerOpen(false); onLogout(); }}
                      style={{
                        background: 'rgba(239, 68, 68, 0.2)',
                        border: '1px solid rgba(239, 68, 68, 0.3)',
                        color: '#ef4444',
                        padding: '8px 14px',
                        borderRadius: '10px',
                        fontSize: '13px',
                        fontWeight: 600,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        backdropFilter: 'blur(10px)'
                      }}
                    >
                      <LogOut size={16} />
                      <span>{t('auth.logout')}</span>
                    </button>
                  )}
                </div>
                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                  <StatBadge icon={<CheckCircle size={16} />} value={`${stats.pendingLeaves} ${t('navigation.leaves')}`} isMobile={true} />
                  <StatBadge icon={<DollarSign size={16} />} value={`${stats.pendingExpenses} ${t('navigation.expenses')}`} isMobile={true} />
                  <StatBadge icon={<UserPlus size={16} />} value={`${stats.pendingRegistrations} ${t('admin.registrations')}`} isMobile={true} />
                  <StatBadge icon={<Users size={16} />} value={`${stats.totalEmployees} ${t('navigation.employees')}`} isMobile={true} />
                </div>
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="admin-header" style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          padding: '24px 20px 20px',
          background: 'transparent',
          backdropFilter: 'none',
          borderBottom: 'none',
          zIndex: 999,
          maxWidth: '100%'
        }}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'flex-start',
            marginBottom: '6px'
          }}>
            <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: '12px' }}>
              {(user?.company_logo_url || user?.companyLogoUrl) && (
                <div
                  title={t('common.change_company_logo') || 'Change company logo'}
                  style={{
                    width: 50,
                    height: 50,
                    borderRadius: '50%',
                    overflow: 'hidden',
                    border: '1px solid rgba(255,255,255,0.4)',
                    flexShrink: 0,
                    background: 'rgba(15,23,42,0.9)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <img
                    src={(() => {
                      const logo = user.company_logo_url || user.companyLogoUrl;
                      return /^https?:\/\//i.test(String(logo || '')) ? logo : `${API_BASE.replace(/\/$/, '')}/${logo}`;
                    })()}
                    alt={user?.company || user?.companyName || 'Logo'}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                </div>
              )}
              <div style={{ 
                fontSize: 'clamp(28px, 5vw, 40px)', 
                fontWeight: 700, 
                maxWidth: '100%',
                letterSpacing: '-0.5px',
                lineHeight: 1.2,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
                {getGreeting()} {user?.first_name || 'Admin'}{(user?.company || user?.companyName) ? ` - ${user?.company || user?.companyName}` : ''}
              </div>
            </div>
            <div style={{ display: 'flex', gap: '12px', flexShrink: 0, alignItems: 'center' }}>
              <button
                onClick={() => onNavigate && onNavigate('home')}
                style={{
                  background: 'rgba(255, 255, 255, 0.1)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  color: '#ffffff',
                  padding: '8px 14px',
                  borderRadius: '10px',
                  fontSize: '13px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  transition: 'all 0.2s',
                  backdropFilter: 'blur(10px)'
                }}
              >
                <Home size={16} />
                <span style={{ fontSize: '12px' }}>{t('navigation.home')}</span>
              </button>
              {onLogout && (
                <button
                  onClick={onLogout}
                  style={{
                    background: 'rgba(239, 68, 68, 0.2)',
                    border: '1px solid rgba(239, 68, 68, 0.3)',
                    color: '#ef4444',
                    padding: '8px 14px',
                    borderRadius: '10px',
                    fontSize: '13px',
                    fontWeight: 600,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    transition: 'all 0.2s',
                    backdropFilter: 'blur(10px)'
                  }}
                >
                  <LogOut size={16} />
                  <span style={{ fontSize: '12px' }}>{t('auth.logout')}</span>
                </button>
              )}
            </div>
          </div>
          <div style={{ 
            fontSize: 'clamp(14px, 2.5vw, 18px)', 
            color: 'rgba(255, 255, 255, 0.7)',
            maxWidth: '100%',
            letterSpacing: '0.2px',
            fontWeight: 500,
            marginBottom: '18px'
          }}>
            {formatDate(currentTime)}
          </div>
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            <StatBadge icon={<CheckCircle size={16} />} value={`${stats.pendingLeaves} ${t('navigation.leaves')}`} isMobile={isMobile} />
            <StatBadge icon={<DollarSign size={16} />} value={`${stats.pendingExpenses} ${t('navigation.expenses')}`} isMobile={isMobile} />
            <StatBadge icon={<UserPlus size={16} />} value={`${stats.pendingRegistrations} ${t('admin.registrations')}`} isMobile={isMobile} />
            <StatBadge icon={<Users size={16} />} value={`${stats.totalEmployees} ${t('navigation.employees')}`} isMobile={isMobile} />
          </div>
        </div>
      )}

      {/* Main App Grid */}
      <div className="admin-grid" style={{
        padding: isMobile ? '72px 16px 100px' : '200px 16px 120px',
        display: 'grid',
        gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)',
        gap: isMobile ? '16px' : '20px',
        maxWidth: '1600px',
        margin: '0 auto'
      }}>
      <input
        id="company-logo-input"
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={handleCompanyLogoChange}
      />
        {/* 1. Dashboard KPI - sirf jab plan mein dashboard ho */}
        {hasFeature(enabledFeatures, 'dashboard', billingLoaded) && (
        <LargeWidget
          title={t('dashboard.title')}
          subtitle={t('dashboard.kpi_performances')}
          icon={<BarChart3 size={56} />}
          gradient={colorPalette.dashboard.gradient}
          shadow={colorPalette.dashboard.shadow}
          onClick={() => handleAppClick('dashboard')}
          delay={0}
          isMobile={isMobile}
        />
        )}

        {/* 2. Rapports */}
        {hasFeature(enabledFeatures, 'reports', billingLoaded) && (
        <AppIcon
          icon={<TrendingUp size={36} />}
          label={t('navigation.reports')}
          color={colorPalette.reports.gradient}
          shadow={colorPalette.reports.shadow}
          onClick={() => handleAppClick('reports')}
          delay={1}
          isMobile={isMobile}
        />
        )}

        {/* 3. Gestion des Soldes */}
        {hasFeature(enabledFeatures, 'soldes', billingLoaded) && (
        <AppIcon
          icon={<Wallet size={36} />}
          label={t('balance.title')}
          color={colorPalette.soldes.gradient}
          shadow={colorPalette.soldes.shadow}
          onClick={() => handleAppClick('soldes')}
          delay={2}
          isMobile={isMobile}
        />
        )}

        {/* 4. Planning d'Équipe */}
        {hasFeature(enabledFeatures, 'planning', billingLoaded) && (
        <AppIcon
          icon={<Calendar size={36} />}
          label={t('navigation.planning')}
          color={colorPalette.planning.gradient}
          shadow={colorPalette.planning.shadow}
          onClick={() => handleAppClick('planning')}
          delay={3}
          isMobile={isMobile}
        />
        )}

        {/* 5. Équipe */}
        {hasFeature(enabledFeatures, 'employees', billingLoaded) && (
        <AppIcon
          icon={<Users size={36} />}
          label={t('navigation.employees')}
          color={colorPalette.employees.gradient}
          shadow={colorPalette.employees.shadow}
          onClick={() => handleAppClick('employees')}
          delay={4}
          isMobile={isMobile}
        />
        )}

        {/* 6. Badges & QR */}
        {hasFeature(enabledFeatures, 'badges', billingLoaded) && (
        <AppIcon
          icon={<QrCode size={36} />}
          label={t('navigation.badges')}
          color={colorPalette.badges.gradient}
          shadow={colorPalette.badges.shadow}
          onClick={() => handleAppClick('badges')}
          delay={5}
          isMobile={isMobile}
        />
        )}

        {/* 7. Paie - Masqué */}
        {/* <AppIcon
          icon={<Wallet size={36} />}
          label="Paie"
          color={colorPalette.payroll.gradient}
          shadow={colorPalette.payroll.shadow}
          onClick={() => handleAppClick('payroll')}
          delay={6}
          isMobile={isMobile}
        /> */}

        {/* 8. Demandes de Congés - Masqué */}
        {/* <AppIcon
          icon={<Calendar size={36} />}
          label="Congés"
          badge={stats.pendingLeaves}
          color={colorPalette['leaves-admin'].gradient}
          shadow={colorPalette['leaves-admin'].shadow}
          onClick={() => handleAppClick('leaves')}
          delay={7}
          isMobile={isMobile}
        /> */}

        {/* 9. Notes de Frais - Masqué */}
        {/* <AppIcon
          icon={<DollarSign size={36} />}
          label="Notes de Frais"
          badge={stats.pendingExpenses}
          color={colorPalette['expenses-admin'].gradient}
          shadow={colorPalette['expenses-admin'].shadow}
          onClick={() => handleAppClick('notes-frais')}
          delay={8}
          isMobile={isMobile}
        /> */}

        {/* 10. Demandes d'Inscription - Masqué */}
        {/* <AppIcon
          icon={<UserPlus size={36} />}
          label="Inscriptions"
          badge={stats.pendingRegistrations}
          color={colorPalette.registrations.gradient}
          shadow={colorPalette.registrations.shadow}
          onClick={() => handleAppClick('admin-panel')}
          delay={9}
          isMobile={isMobile}
        /> */}

        {/* 11. Gestion des Comptes - Masqué */}
        {/* <AppIcon
          icon={<Users size={36} />}
          label="Comptes"
          color={colorPalette.accounts.gradient}
          shadow={colorPalette.accounts.shadow}
          onClick={() => handleAppClick('admin-panel')}
          delay={10}
          isMobile={isMobile}
        /> */}

        {/* 12. Audit Photos */}
        {hasFeature(enabledFeatures, 'audit_photos', billingLoaded) && (
        <AppIcon
          icon={<Camera size={36} />}
          label="Audit Photos"
          color={colorPalette['audit-photos'].gradient}
          shadow={colorPalette['audit-photos'].shadow}
          onClick={() => handleAppClick('audit-photos')}
          delay={11}
          isMobile={isMobile}
        />
        )}

        {/* 13. Facturation - admin ko hamesha (plan manage krne ke liye) */}
        <AppIcon
          icon={<CreditCard size={36} />}
          label="Facturation"
          color={colorPalette.facturation.gradient}
          shadow={colorPalette.facturation.shadow}
          onClick={() => handleAppClick('billing')}
          delay={12}
          isMobile={isMobile}
        />

        {/* 14. Upload logo (admin/manager) */}
        {['admin', 'manager'].includes(user?.role) && (
        <AppIcon
          icon={<Shield size={36} />}
          label="Upload logo"
          color="linear-gradient(135deg, #0ea5e9, #22c55e)"
          shadow="rgba(16, 185, 129, 0.35)"
          onClick={() => {
            const input = document.getElementById('company-logo-input');
            if (input) input.click();
          }}
          delay={13}
          isMobile={isMobile}
        />
        )}

        {/* 15. Plans - admin ko hamesha (plan upgrade ke liye) */}
        <AppIcon
          icon={<Sparkles size={36} />}
          label="Plans"
          color={colorPalette.abonnements.gradient}
          shadow={colorPalette.abonnements.shadow}
          onClick={() => handleAppClick('billing-plans')}
          delay={14}
          isMobile={isMobile}
        />

        {/* 16. Configuration GPS - sirf jab pointage plan mein ho */}
        {hasFeature(enabledFeatures, 'pointage', billingLoaded) && (
        <AppIcon
          icon={<MapPin size={36} />}
          label={t('navigation.gps_config')}
          color={colorPalette.gps.gradient}
          shadow={colorPalette.gps.shadow}
          onClick={() => handleAppClick('geolocation-admin')}
          delay={14}
          isMobile={isMobile}
        />
        )}

      </div>
    </div>
  );
};

// Composant StatBadge
const StatBadge = ({ icon, value, isMobile = false }) => (
  <div style={{
    background: 'rgba(255, 255, 255, 0.1)',
    backdropFilter: 'blur(10px)',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    padding: '8px 16px',
    borderRadius: '20px',
    fontSize: isMobile ? '14px' : '13px',
    fontWeight: 600,
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.1)',
    lineHeight: '1.3',
    padding: isMobile ? '10px 18px' : '8px 16px'
  }}>
    {typeof icon === 'string' ? <span style={{ fontSize: '16px' }}>{icon}</span> : icon}
    <span>{value}</span>
  </div>
);

// Composant LargeWidget
const LargeWidget = ({ title, subtitle, icon, gradient, shadow, onClick, delay = 0, isMobile = false }) => (
  <div
    className="admin-app-icon admin-large-widget"
    onClick={onClick}
    style={{
      gridColumn: 'span 2',
      aspectRatio: '2/1',
      background: gradient,
      borderRadius: '24px',
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: isMobile ? '28px' : '24px',
      cursor: 'pointer',
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      position: 'relative',
      overflow: 'hidden',
      boxShadow: `0 8px 32px ${shadow}, 0 4px 16px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.1)`,
      animation: `slideUp 0.4s ease-out ${delay * 0.05}s backwards`,
      willChange: 'transform',
      minHeight: isMobile ? '140px' : 'auto',
      touchAction: 'manipulation',
      WebkitTapHighlightColor: 'rgba(255, 255, 255, 0.1)'
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.transform = 'translateY(-4px) scale(1.02)';
      e.currentTarget.style.boxShadow = `0 12px 40px ${shadow}, 0 6px 20px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.15)`;
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.transform = 'scale(1)';
      e.currentTarget.style.boxShadow = `0 8px 32px ${shadow}, 0 4px 16px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.1)`;
    }}
  >
    <div style={{ position: 'relative', zIndex: 2 }}>
      <div style={{ fontSize: isMobile ? '20px' : '18px', fontWeight: 700, marginBottom: '6px', letterSpacing: '-0.3px' }}>
        {title}
      </div>
      <div style={{ fontSize: isMobile ? '16px' : '13px', color: 'rgba(255,255,255,0.8)', fontWeight: 500 }}>
        {subtitle}
      </div>
    </div>
    <div style={{ 
      fontSize: isMobile ? '64px' : '56px', 
      position: 'relative', 
      zIndex: 2,
      filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.3))',
      opacity: 0.95
    }}>
      {icon}
    </div>
    <div style={{
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0) 100%)',
      pointerEvents: 'none',
      zIndex: 1
    }} />
  </div>
);

// Composant AppIcon
const AppIcon = ({ icon, label, badge, color, shadow, onClick, delay = 0, isMobile = false }) => {
  return (
    <div
      className="admin-app-icon"
      onClick={onClick}
      style={{
        aspectRatio: '1',
        background: color || '#1e293b',
        borderRadius: '20px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: isMobile ? '16px' : '10px',
        cursor: 'pointer',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        position: 'relative',
        overflow: 'hidden',
        boxShadow: shadow ? `0 4px 16px ${shadow}, 0 2px 8px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.1)` : '0 4px 16px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.05)',
        animation: `slideUp 0.4s ease-out ${delay * 0.05}s backwards`,
        willChange: 'transform',
        minHeight: isMobile ? '120px' : '100px',
        padding: isMobile ? '20px 12px' : undefined,
        touchAction: 'manipulation',
        WebkitTapHighlightColor: 'rgba(255, 255, 255, 0.1)'
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-6px) scale(1.02)';
        e.currentTarget.style.boxShadow = shadow ? `0 12px 32px ${shadow}, 0 4px 16px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.15)` : '0 12px 32px rgba(0,0,0,0.4), 0 4px 16px rgba(0,0,0,0.2)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'scale(1)';
        e.currentTarget.style.boxShadow = shadow ? `0 4px 16px ${shadow}, 0 2px 8px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.1)` : '0 4px 16px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.05)';
      }}
    >
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0) 100%)',
        pointerEvents: 'none',
        zIndex: 1
      }} />
      <div style={{ 
        fontSize: isMobile ? '48px' : '44px', 
        filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))',
        position: 'relative',
        zIndex: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        {icon}
      </div>
      <div style={{
        fontSize: isMobile ? '16px' : '12px',
        fontWeight: 600,
        textAlign: 'center',
        maxWidth: '100%',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        padding: isMobile ? '0 12px' : '0 8px',
        position: 'relative',
        zIndex: 2,
        letterSpacing: '0.1px',
        lineHeight: '1.3'
      }}>
        {label}
      </div>
      {badge && badge > 0 && (
        <div style={{
          position: 'absolute',
          top: '8px',
          right: '8px',
          background: 'linear-gradient(135deg, #ef4444, #f87171)',
          color: 'white',
          fontSize: '11px',
          fontWeight: 700,
          minWidth: '20px',
          height: '20px',
          borderRadius: '10px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '0 6px',
          boxShadow: '0 2px 8px rgba(239,68,68,0.4), inset 0 1px 0 rgba(255,255,255,0.2)',
          zIndex: 3
        }}>
          {badge > 99 ? '99+' : badge}
        </div>
      )}
    </div>
  );
};

export default AdminDashboardHome;

